import React, { Component } from "react";
import Header from "../components/Header";
import HelpBadge from "../components/HelpBadge";
import Navigation from "../components/Navigation";
import Section from "../components/Section";

import { SECTION, CARD, SECTION_MAPPING } from "../constants";
import { nestedData, section, inView } from "../global-interfaces";
import {
  updateURL,
  scrollToElement,
  getURLParams,
  parseCardID,
  checkIfMobile,
} from "../utils";
import { timeout } from "d3";

interface Props {
  data: nestedData[];
}

interface State {
  section: section;
  inView: inView;
  isMobile: boolean;
  isNavOpen: boolean;
}

const urlParams = getURLParams();

class App extends Component<Props, State> {
  observer: IntersectionObserver;
  observerOptions: {
    root: null;
    threshold: number;
  };
  sectionHeights: number[];
  tabHeight: number;

  constructor(props: Props) {
    super(props);
    this.state = {
      section: urlParams[SECTION],
      isMobile: checkIfMobile(),
      inView: {},
      isNavOpen: false,
    };
    /** OBSERVER CONFIG
     * ref: https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
     */
    this.observerOptions = {
      root: null, // when null, defaults to viewport, which is what we want
      threshold: this.state.isMobile ? 0.1 : 0.25, // % of target's visibility that should trigger the callback
    };
    this.observer = new IntersectionObserver(
      this.handleObserver.bind(this),
      this.observerOptions
    );
    this.sectionHeights = [];
    this.tabHeight = 0;

    this.setSection = this.setSection.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.onScroll = this.onScroll.bind(this);
  }

  handleObserver(entries: IntersectionObserverEntry[]) {
    const { section: prevSection } = this.state;

    // 1. set section if scrolled into new section
    let section: section | undefined;
    let cardInView = entries.find(d => d.isIntersecting);
    if (cardInView) {
      const [sectionHash, _] = parseCardID(cardInView.target.id);
      if (sectionHash !== prevSection.hash) {
        section = SECTION_MAPPING.find(d => d.hash === sectionHash);
      }
    }
    // 2. update inView for all entries
    this.setState(prevState => ({
      ...prevState,
      inView: {
        ...entries.reduce((a: inView, c) => {
          const [sectionHash, card] = parseCardID(c.target.id);
          // https://redux.js.org/recipes/structuring-reducers/immutable-update-patterns#common-mistake-2-only-making-a-shallow-copy-of-one-level -- needed to not mutate state
          return {
            ...a,
            [sectionHash]: { ...a[sectionHash], [card]: c.isIntersecting },
          };
        }, prevState.inView),
      },
      // ...(section ? { section } : {}),
    }));
  }

  componentDidMount() {
    const { section } = this.state;
    // position calculations aren't accurate immediately, need a little buffer
    // issue: https://github.com/interactivethings/catalog/issues/195
    timeout(() => scrollToElement(section, urlParams[CARD]), 500);

    window.addEventListener("resize", this.handleResize);
    this.handleResize();

    (document.querySelector(
      ".sections-wrapper"
    ) as HTMLDivElement).addEventListener("scroll", this.onScroll);
  }

  handleResize() {
    const isMobile = checkIfMobile();
    if (isMobile !== this.state.isMobile) this.setState({ isMobile });

    // iOS Height Fix: https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);

    const sectionsWrapper = document.querySelector(
      ".sections-wrapper"
    ) as HTMLDivElement;
    this.tabHeight = Math.round(
      sectionsWrapper.offsetHeight / SECTION_MAPPING.length
    );
    setTimeout(() => {
      this.sectionHeights = [...sectionsWrapper.children]
        .map((e: any) => e.offsetHeight)
        .reduce((t, v, i) => [...t, (t[i - 1] ? t[i - 1] : 0) + v], []);
    }, 250);
  }

  onScroll(e: any) {
    const { index } = this.state.section;
    const { scrollTop } = e.target;
    let nextIndex = 0;
    this.sectionHeights.forEach((d: number, j: number) => {
      if (scrollTop >= d - (j + 1) * this.tabHeight) nextIndex = j + 1;
    });

    if (nextIndex !== index) {
      let section: section | undefined = SECTION_MAPPING.find(
        d => d.index === nextIndex
      );
      if (section) {
        timeout(() => {
          updateURL(section as section);
        }, 1000);
        this.setState({ section });
      }
    }
  }

  componentWillUnmount() {
    this.observer.disconnect();
    window.removeEventListener("resize", this.handleResize); // prevent memory leaks

    (document.querySelector(
      ".sections-wrapper"
    ) as HTMLDivElement).removeEventListener("scroll", this.onScroll); // prevent memory leaks
  }

  setSection(section: section) {
    updateURL(section);
    if (section.title !== this.state.section.title) {
      // this.setState(prevState => ({
      //   section,
      // }));
      scrollToElement(section);
    }
  }

  render() {
    const { inView, isMobile, isNavOpen } = this.state;

    const introInView = inView[SECTION_MAPPING[0].hash];

    const scrolledPastIntro = introInView
      ? !Object.values(inView[SECTION_MAPPING[0].hash]).reduce((a, c) => {
          return a || c; // see if any of intro cards are currently inView
        }, false)
      : false;

    return (
      <div className="App">
        <Header scrolledPastIntro={scrolledPastIntro} />
        <HelpBadge isMobile={isMobile} isNavOpen={isNavOpen} />
        {/* TODO: add flag for whether navigation is open or not */}
        <div className="main">
          <Navigation
            setSection={this.setSection}
            selectedSection={this.state.section}
            isMobile={isMobile}
            isOpen={isNavOpen}
            setNavStatus={(isNavOpen: boolean) => this.setState({ isNavOpen })}
          />
          <div
            className="sections-wrapper"
            // for iOS - main body needs to be 'focusable' in order to toggle navitation
            tabIndex={0}
          >
            {this.props.data.map((d: nestedData, i: number) => (
              <Section
                key={d.index + d.title}
                data={d}
                setSection={this.setSection}
                observer={this.observer}
                inView={inView[d.hash]}
                isMobile={isMobile}
                isLast={i === this.props.data.length - 1}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }
}

export default App;
